import type { CameraAngles, HotspotType, Position3D } from "@/types";
import sphereCorrectionLive from "@/lib/sphereCorrectionLive";
import {
  useCurrentScene,
  useCurrentSceneId,
  useSelectScene,
  useTour,
  useTourAllScenes,
} from "@h/useCtx";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  useAutoPlay,
  useIsEditMode,
  useSelectedFloorId,
  useSetSelectedFloorId,
} from "./ViewerContextHooks";
import {
  AmbientAudioContext,
  ViewerDrawingContext,
  ViewerHotspotFocusContext,
  ViewerPlacementContext,
  ViewerUIContext,
  type AmbientAudioValue,
  type CaptureMode,
  type ViewerDrawingValue,
  type ViewerHotspotFocusValue,
  type ViewerPlacementValue,
  type ViewerUIValue,
} from "./ViewerContexts";

const SyncWorker = () => {
  const currentScene = useCurrentScene();
  const setSelectedFloorId = useSetSelectedFloorId();
  const selectedFloorId = useSelectedFloorId();
  const autoPlay = useAutoPlay();
  const allScenes = useTourAllScenes();
  const selectScene = useSelectScene();
  const sceneId = useCurrentSceneId();
  const tour = useTour();

  useEffect(() => {
    if (currentScene?.floor_id && !selectedFloorId) {
      setSelectedFloorId(currentScene.floor_id);
    }
  }, [currentScene?.floor_id, selectedFloorId, setSelectedFloorId]);

  const apSettings = tour?.settings?.autoplay;

  const autoplayScenes = useMemo(() => {
    if (!apSettings || apSettings.preset === "all") return allScenes;
    if (apSettings.preset === "main_per_room") {
      const filtered = allScenes.filter((s) => s.scene_is_main);
      return filtered.length > 0 ? filtered : allScenes;
    }
    if (apSettings.preset === "custom" && apSettings.custom_scene_ids?.length) {
      const ordered = apSettings.custom_scene_ids
        .map((id) => allScenes.find((s) => s.scene_id === id))
        .filter(Boolean) as typeof allScenes;
      return ordered.length > 0 ? ordered : allScenes;
    }
    return allScenes;
  }, [apSettings, allScenes]);

  const durationMs = (apSettings?.duration_sec ?? 25) * 1000;
  const shouldLoop = apSettings?.loop ?? true;

  useEffect(() => {
    if (!autoPlay || autoplayScenes.length <= 1) return;
    const interval = setInterval(() => {
      const idx = autoplayScenes.findIndex((s) => s.scene_id === sceneId);
      const currentIdx = idx >= 0 ? idx : 0;
      if (!shouldLoop && currentIdx === autoplayScenes.length - 1) return;
      const nextIdx = (currentIdx + 1) % autoplayScenes.length;
      selectScene(autoplayScenes[nextIdx].scene_id);
    }, durationMs);
    return () => clearInterval(interval);
  }, [autoPlay, autoplayScenes, sceneId, selectScene, durationMs, shouldLoop]);

  return null;
};

interface AudioSyncWorkerProps {
  onTracksChange: (tracks: string[]) => void;
}

// Resolve the active ambient source into an ordered list of URLs. Scene/room
// audio is always a single track; the tour tier can be a playlist
// (settings.playlist), falling back to the legacy single tour_audio.
const AudioSyncWorker = ({ onTracksChange }: AudioSyncWorkerProps) => {
  const currentScene = useCurrentScene();
  const tour = useTour();

  const tracks = useMemo<string[]>(() => {
    if (currentScene?.scene_audio_url) return [currentScene.scene_audio_url];
    if (currentScene?.room_audio) return [currentScene.room_audio];
    const playlist = tour?.settings?.playlist ?? [];
    if (playlist.length > 0) return playlist.map((t) => t.url).filter(Boolean);
    return currentScene?.tour_audio ? [currentScene.tour_audio] : [];
  }, [currentScene, tour?.settings?.playlist]);

  const key = tracks.join("|");
  useEffect(() => {
    onTracksChange(tracks);
    // key is a stable string proxy for the tracks array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, onTracksChange]);

  return null;
};

type AudioState = {
  isPlaying: boolean;
  volume: number;
  progress: number;
  duration: number;
};

type AudioAction =
  | { type: "RESET" }
  | { type: "SYNC_EXISTING"; isPlaying: boolean; progress: number; duration: number }
  | { type: "NEW_AUDIO" }
  | { type: "SET_PLAYING"; isPlaying: boolean }
  | { type: "SET_PROGRESS"; progress: number }
  | { type: "SET_DURATION"; duration: number }
  | { type: "SET_VOLUME"; volume: number }
  | { type: "SEEK"; progress: number };

const initialAudioState: AudioState = { isPlaying: false, volume: 0.5, progress: 0, duration: 0 };

function audioReducer(state: AudioState, action: AudioAction): AudioState {
  switch (action.type) {
    case "RESET":        return { ...state, isPlaying: false, progress: 0 };
    case "SYNC_EXISTING": return { ...state, isPlaying: action.isPlaying, progress: action.progress, duration: action.duration };
    case "NEW_AUDIO":    return { ...state, isPlaying: false, progress: 0 };
    case "SET_PLAYING":  return { ...state, isPlaying: action.isPlaying };
    case "SET_PROGRESS": return { ...state, progress: action.progress };
    case "SET_DURATION": return { ...state, duration: action.duration };
    case "SET_VOLUME":   return { ...state, volume: action.volume };
    case "SEEK":         return { ...state, progress: action.progress };
  }
}

/**
 * iOS ignores writes to HTMLMediaElement.volume (system-volume only), so the
 * slider is a no-op there. Detected once, lazily: on those devices we route the
 * element through a Web Audio GainNode instead.
 */
let nativeVolumeSupport: boolean | null = null;
const hasNativeVolume = () => {
  if (nativeVolumeSupport === null) {
    const probe = new Audio();
    probe.volume = 0.5;
    nativeVolumeSupport = probe.volume === 0.5;
  }
  return nativeVolumeSupport;
};

const AmbientAudioProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const isEditMode = useIsEditMode();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const [audioState, dispatch] = useReducer(audioReducer, initialAudioState);
  const { isPlaying, volume, progress, duration } = audioState;
  const volumeRef = useRef(volume);
  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);
  const [tracks, setTracks] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const activeUrl = tracks[index] ?? null;
  const multi = tracks.length > 1;
  const tracksKeyRef = useRef("");

  // A MediaElementSource is bound to one element for life — tear the graph down
  // whenever the element is replaced.
  const teardownGain = useCallback(() => {
    gainRef.current = null;
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
  }, []);

  const applyVolume = useCallback((audio: HTMLAudioElement, vol: number) => {
    if (hasNativeVolume()) {
      audio.volume = vol;
      return;
    }
    if (!gainRef.current) {
      try {
        const Ctx =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new Ctx();
        const gain = ctx.createGain();
        ctx.createMediaElementSource(audio).connect(gain).connect(ctx.destination);
        audioCtxRef.current = ctx;
        gainRef.current = gain;
      } catch {
        return;
      }
    }
    audioCtxRef.current?.resume().catch(() => {});
    gainRef.current.gain.value = vol;
  }, []);

  const handleTracksChange = useCallback((next: string[]) => {
    const key = next.join("|");
    if (key === tracksKeyRef.current) return;
    tracksKeyRef.current = key;
    setTracks(next);
    setIndex(0);
  }, []);

  useEffect(() => {
    if (!activeUrl) {
      dispatch({ type: "RESET" });
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
        teardownGain();
      }
      return;
    }

    const absoluteActiveUrl = new URL(activeUrl, window.location.origin).href;
    let audio = audioRef.current;
    let isNewAudio = false;

    if (audio && audio.src === absoluteActiveUrl) {
      dispatch({ type: "SYNC_EXISTING", isPlaying: !audio.paused, progress: audio.currentTime, duration: audio.duration || 0 });
    } else {
      if (audio) {
        audio.pause();
        audio.src = "";
        teardownGain();
      }
      audio = new Audio();
      // Web Audio needs CORS-clean media, and crossOrigin must be set before src.
      if (!hasNativeVolume()) audio.crossOrigin = "anonymous";
      audio.src = activeUrl;
      audioRef.current = audio;
      applyVolume(audio, volumeRef.current);
      isNewAudio = true;

      dispatch({ type: "NEW_AUDIO" });
    }

    // Single track loops; a playlist advances on end (loop back at the end).
    audio.loop = !multi;
    audio.onplay = () => dispatch({ type: "SET_PLAYING", isPlaying: true });
    audio.onpause = () => dispatch({ type: "SET_PLAYING", isPlaying: false });
    audio.onended = () => {
      if (multi) setIndex((i) => (i + 1) % tracks.length);
      else dispatch({ type: "SET_PLAYING", isPlaying: false });
    };
    audio.ontimeupdate = () => dispatch({ type: "SET_PROGRESS", progress: audio.currentTime });
    audio.onloadedmetadata = () => dispatch({ type: "SET_DURATION", duration: audio.duration });

    let removeRetryListeners: (() => void) | null = null;

    if (isNewAudio && !isEditMode) {
      audio.play().catch(() => {
        let pending = false;
        const unlock = () => {
          if (pending) return;
          pending = true;
          audio
            ?.play()
            .then(() => {
              removeRetryListeners?.();
              removeRetryListeners = null;
            })
            .catch(() => {
              pending = false;
            });
        };

        document.addEventListener("touchstart", unlock);
        document.addEventListener("touchend", unlock);
        document.addEventListener("click", unlock);

        removeRetryListeners = () => {
          document.removeEventListener("touchstart", unlock);
          document.removeEventListener("touchend", unlock);
          document.removeEventListener("click", unlock);
        };
      });
    }

    return () => {
      removeRetryListeners?.();
      if (audio) {
        audio.onplay = null;
        audio.onpause = null;
        audio.onended = null;
        audio.ontimeupdate = null;
        audio.onloadedmetadata = null;
      }
    };
  }, [activeUrl, multi, tracks, isEditMode, applyVolume, teardownGain]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
        teardownGain();
      }
    };
  }, [teardownGain]);

  // Mobile: con la pestaña oculta, la app en segundo plano o la pantalla
  // bloqueada, el audio seguía sonando fuera del navegador. Se pausa todo lo que
  // suena en el viewer — el ambiente y cualquier <audio>/<video> del DOM (videos
  // de hotspots) — y NO se reanuda solo al volver: el visitante decide.
  // El audio de AudioHotspot es un `new Audio()` fuera del DOM y tiene su propio
  // listener con la misma regla.
  useEffect(() => {
    const pauseAll = () => {
      audioRef.current?.pause();
      document
        .querySelectorAll<HTMLMediaElement>("audio, video")
        .forEach((m) => m.pause());
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") pauseAll();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", pauseAll);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", pauseAll);
    };
  }, []);

  const toggle = useCallback(() => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioCtxRef.current?.resume().catch(() => {});
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, []);

  const setVolume = useCallback((vol: number) => {
    const safeVol = Math.max(0, Math.min(1, vol));
    if (audioRef.current) applyVolume(audioRef.current, safeVol);
    dispatch({ type: "SET_VOLUME", volume: safeVol });
  }, [applyVolume]);

  const seek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      dispatch({ type: "SEEK", progress: time });
    }
  }, []);

  const trackCount = tracks.length;
  const next = useCallback(() => {
    setIndex((i) => (trackCount ? (i + 1) % trackCount : 0));
  }, [trackCount]);
  const prev = useCallback(() => {
    setIndex((i) => (trackCount ? (i - 1 + trackCount) % trackCount : 0));
  }, [trackCount]);

  const value = useMemo<AmbientAudioValue>(
    () => ({
      isPlaying,
      volume,
      progress,
      duration,
      hasAudio: !!activeUrl,
      audioRef,
      toggle,
      setVolume,
      seek,
      trackCount,
      trackIndex: index,
      next,
      prev,
    }),
    [isPlaying, volume, progress, duration, activeUrl, toggle, setVolume, seek, trackCount, index, next, prev],
  );

  return (
    <AmbientAudioContext.Provider value={value}>
      <AudioSyncWorker onTracksChange={handleTracksChange} />
      {children}
    </AmbientAudioContext.Provider>
  );
};

export const ViewerProvider = ({
  children,
  initialEditMode = false,
}: {
  children: React.ReactNode;
  initialEditMode?: boolean;
}) => {
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [autoPlay, setAutoPlay] = useState(false);
  const [hudVisible, setHudVisible] = useState(true);
  const [isMinimizedMap, setIsMinimizedMap] = useState(true);
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
  const [focusedRoomId, setFocusedRoomId] = useState<string | null>(null);
  const [hoveredRoomId, setHoveredRoomId] = useState<string | null>(null);
  const [placementMode, setPlacementMode] = useState<HotspotType | null>(null);
  const [drawingType, setDrawingType] = useState<
    "Area" | "Video" | "Mask" | null
  >(null);
  const [drawPoints, setDrawPoints] = useState<Position3D[]>([]);
  const [drawEditingId, setDrawEditingId] = useState<string | null>(null);
  const [focusedHsId, setFocusedHsId] = useState<string | null>(null);
  const [movingHsId, setmovingHsId] = useState<string | null>(null);
  const [captureMode, setCaptureMode] = useState<CaptureMode>(null);
  const [sphereCorrection, setSphereCorrection] = useState<import("./ViewerContexts").SphereCorrectionState | null>(null);
  // El visitante abre con el navegador de escenas COLAPSADO — la foto primero,
  // los controles a pedido (el minimapa ya arrancaba minimizado). En edición
  // arranca abierto: es la herramienta de trabajo del dueño.
  const [isSceneBrowserVisible, setIsSceneBrowserVisible] =
    useState(initialEditMode);
  const cameraAnglesRef = useRef<CameraAngles>({ azimuth: 0, polar: 0 });

  const handleSetSelectedFloorId: React.Dispatch<
    React.SetStateAction<string | null>
  > = useCallback((value) => {
    setSelectedFloorId(value);
    setFocusedRoomId(null);
  }, []);

  // Leaving edit mode (any path: URL, HUD toggle, tour tab) drops every editor
  // sub-mode so none of it bleeds into tour/normal mode. Mirrors the per-tool
  // cancels, centralized on the isEditMode source of truth.
  //
  // El reset del estado de React se hace EN RENDER, en la transición: hacerlo
  // en un efecto encadenaba un render extra por cada salida del editor (y es lo
  // que marca react-hooks/set-state-in-effect). El store externo
  // `sphereCorrectionLive` sí se sincroniza en un efecto, que es para lo que
  // están los efectos.
  const [wasEditMode, setWasEditMode] = useState(isEditMode);
  if (wasEditMode !== isEditMode) {
    setWasEditMode(isEditMode);
    if (!isEditMode) {
      setPlacementMode(null);
      setDrawingType(null);
      setDrawPoints([]);
      setDrawEditingId(null);
      setmovingHsId(null);
      setSphereCorrection(null);
    }
  }
  useEffect(() => {
    if (!isEditMode) sphereCorrectionLive.active = false;
  }, [isEditMode]);

  const isDrawing = drawingType !== null;

  const uiValue = useMemo<ViewerUIValue>(
    () => ({
      isEditMode,
      setIsEditMode,
      autoPlay,
      setAutoPlay,
      hudVisible,
      setHudVisible,
      isMinimizedMap,
      setIsMinimizedMap,
      selectedFloorId,
      handleSetSelectedFloorId,
      focusedRoomId,
      setFocusedRoomId,
      hoveredRoomId,
      setHoveredRoomId,
      captureMode,
      setCaptureMode,
      sphereCorrection,
      setSphereCorrection,
      isSceneBrowserVisible,
      setIsSceneBrowserVisible,
      cameraAnglesRef,
    }),
    [
      isEditMode,
      autoPlay,
      hudVisible,
      isMinimizedMap,
      selectedFloorId,
      focusedRoomId,
      hoveredRoomId,
      captureMode,
      sphereCorrection,
      handleSetSelectedFloorId,
      isSceneBrowserVisible,
      cameraAnglesRef,
    ],
  );

  const placementValue = useMemo<ViewerPlacementValue>(
    () => ({ placementMode, setPlacementMode }),
    [placementMode],
  );

  const drawingValue = useMemo<ViewerDrawingValue>(
    () => ({
      isDrawing,
      drawingType,
      setDrawingType,
      drawPoints,
      setDrawPoints,
      drawEditingId,
      setDrawEditingId,
    }),
    [isDrawing, drawingType, drawPoints, drawEditingId],
  );

  const hotspotFocusValue = useMemo<ViewerHotspotFocusValue>(
    () => ({
      focusedHsId,
      setFocusedHsId,
      movingHsId,
      setmovingHsId,
    }),
    [focusedHsId, movingHsId],
  );

  return (
    <ViewerUIContext.Provider value={uiValue}>
      <ViewerPlacementContext.Provider value={placementValue}>
        <ViewerDrawingContext.Provider value={drawingValue}>
          <ViewerHotspotFocusContext.Provider value={hotspotFocusValue}>
            <AmbientAudioProvider>
              <SyncWorker />
              {children}
            </AmbientAudioProvider>
          </ViewerHotspotFocusContext.Provider>
        </ViewerDrawingContext.Provider>
      </ViewerPlacementContext.Provider>
    </ViewerUIContext.Provider>
  );
};
